import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, ArrowRight, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import toast from 'react-hot-toast';
import { validationRules } from '../../utils/validation';
import { handleFormErrors } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';
import SEO from '../../components/common/SEO';

const ResetPassword = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const { resetPassword } = useAuth();
  // const navigate = useNavigate(); // Uncomment if navigation is needed
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState('validating'); // 'validating', 'valid', 'invalid'
  const [resetComplete, setResetComplete] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors }
  } = useForm();

  const password = watch('password');

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/reset-password`,
          { params: { token } }
        );
        
        if (response.data.success) {
          setTokenStatus('valid');
        } else {
          setTokenStatus('invalid');
          toast.error(response.data.message || 'Invalid or expired reset token');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setTokenStatus('invalid');
        toast.error(error.response?.data?.message || 'Invalid or expired reset token');
      }
    };

    if (token) {
      validateToken();
    } else {
      setTokenStatus('invalid');
      toast.error('Reset token is missing');
    }
  }, [token]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const result = await resetPassword(token, data.password);
      
      if (result.success) {
        setResetComplete(true);
      } else {
        if (result.errors) {
          handleFormErrors(result.errors, setError);
        } else {
          toast.error(result.error || 'Failed to reset password');
        }
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Reset Password"
        description="Create a new password for your Prime Edge account to secure your engineering training profile."
        keywords="reset password, new password, account security, prime edge"
        canonicalUrl="/reset-password"
      />
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-md w-full space-y-8"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center">
          <Link to="/" className="inline-flex items-center space-x-2 mb-6">
            <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">P</span>
            </div>
            <span className="text-2xl font-bold gradient-text">Prime Edge</span>
          </Link>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            {resetComplete ? 'Password Reset Complete' : 'Reset Your Password'}
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            {tokenStatus === 'validating' && 'Validating your reset link...'}
            {tokenStatus === 'valid' && !resetComplete && 'Create a new password for your account'}
            {tokenStatus === 'invalid' && 'This password reset link is invalid or has expired'}
            {resetComplete && 'Your password has been successfully reset'}
          </p>
        </motion.div>

        {/* Form or Status */}
        <motion.div variants={itemVariants}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            {tokenStatus === 'validating' && (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="loading-spinner w-16 h-16 mb-4"></div>
                <p className="text-gray-700 dark:text-gray-300">Please wait while we validate your reset link...</p>
              </div>
            )}

            {tokenStatus === 'invalid' && (
              <div className="flex flex-col items-center justify-center py-6">
                <XCircle className="w-16 h-16 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Invalid Reset Link</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-6 text-center">
                  The password reset link is invalid or has expired. Please request a new password reset link.
                </p>
                <Link to="/forgot-password" className="btn btn-primary btn-lg">
                  Request New Reset Link
                </Link>
              </div>
            )}

            {tokenStatus === 'valid' && !resetComplete && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* New Password Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      {...register('password', validationRules.password)}
                      type={showPassword ? 'text' : 'password'}
                      className="input pl-12 pr-12 w-full"
                      placeholder="Create a new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: value => value === password || 'Passwords do not match'
                      })}
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="input pl-12 pr-12 w-full"
                      placeholder="Confirm your new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary btn-lg w-full inline-flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="loading-spinner w-5 h-5 mr-2"></div>
                      Resetting Password...
                    </>
                  ) : (
                    <>
                      Reset Password
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>
              </form>
            )}

            {resetComplete && (
              <div className="flex flex-col items-center justify-center py-6">
                <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Password Reset Complete!</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-6 text-center">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>
                <Link to="/login" className="btn btn-primary btn-lg">
                  Sign In
                </Link>
              </div>
            )}
          </div>
        </motion.div>

        {/* Help Link */}
        <motion.div variants={itemVariants} className="text-center">
          <p className="text-gray-600 dark:text-gray-300">
            Need help?{' '}
            <Link
              to="/contact"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
            >
              Contact Support
            </Link>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;